import { Link } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';
import SchemaOrg from '@/components/SchemaOrg';

interface SchemaOrgData {
  '@context': string;
  '@type': string;
  [key: string]: unknown;
}

const SITE_URL = 'https://yoybuy.beauty';

const articleSchema: SchemaOrgData = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'YOYBUY Spreadsheet: Полное руководство 2026',
  description: 'Всё, что нужно знать о системе spreadsheet-шоппинга из Китая: как работает YOYBUY Spreadsheet, пошаговая инструкция, сравнение платформ (YOYBUY vs Kakobuy vs Superbuy) и советы для начинающих.',
  url: SITE_URL + '/article/yoybuy-spreadsheet-guide',
  inLanguage: 'ru',
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  author: {
    '@type': 'Organization',
    name: 'YOYBUY Beauty',
    url: SITE_URL + '/',
  },
  publisher: {
    '@type': 'Organization',
    name: 'YOYBUY Beauty',
    url: SITE_URL + '/',
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': SITE_URL + '/article/yoybuy-spreadsheet-guide',
  },
};

export default function YoybuySpreadsheetGuide() {
  return (
    <>
      <SchemaOrg schema={articleSchema} />
      <main>
        <ArticleHero />
        <ArticleContent />
        <CTABlock />
        <Footer />
      </main>
    </>
  );
}

function ArticleHero() {
  return (
    <section className="relative w-full min-h-[520px] md:min-h-[600px] flex flex-col items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20minimal%20editorial%20flat%20lay%20with%20laptop%20spreadsheet%20and%20fashion%20accessories%20on%20cream%20desk%2C%20pastel%20mint%20and%20beige%20color%20palette%2C%20soft%20natural%20light%20from%20window%2C%20scandinavian%20workspace%20aesthetic%2C%20clean%20organized%20desk%20with%20notebook%20and%20coffee%2C%20calm%20productive%20atmosphere%2C%20high%20end%20blog%20photography%20style&width=1600&height=900&seq=article-hero-yoybuy&orientation=landscape"
          alt="YOYBUY Spreadsheet руководство"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/30 to-white/65"></div>
      </div>

      <div className="relative z-10 w-full max-w-[800px] mx-auto px-4 md:px-6 flex flex-col items-center text-center pt-16">
        <nav className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-accent-700 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Вернуться на главную
          </Link>
        </nav>

        <h1 className="text-3xl md:text-[52px] font-heading font-bold text-foreground-950 leading-tight">
          YOYBUY Spreadsheet
        </h1>
        <p className="mt-2 text-2xl md:text-[40px] font-heading italic text-foreground-700">
          Полное руководство 2026
        </p>
        <p className="mt-5 text-base md:text-lg text-foreground-500 font-light max-w-[580px] leading-relaxed">
          Всё, что нужно знать о системе spreadsheet-шоппинга из Китая: как работает YOYBUY Spreadsheet, пошаговая инструкция, сравнение платформ и советы для начинающих.
        </p>

        <a
          href="https://kaspreadsheet.com"
          target="_blank"
          rel="noopener nofollow"
          className="mt-8 group inline-flex items-center gap-3 h-[52px] px-8 rounded-full bg-white border border-foreground-200/60 text-foreground-800 text-base font-medium hover:bg-accent-50 hover:border-accent-300 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          Начать использовать YOYBUY Spreadsheet
          <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center group-hover:bg-accent-600 transition-colors">
            <i className="ri-arrow-right-line text-white text-xs"></i>
          </span>
        </a>
      </div>
    </section>
  );
}

function ArticleContent() {
  return (
    <article className="bg-background-50 py-16 md:py-20 px-4 md:px-6">
      <div className="max-w-[760px] mx-auto">
        {/* SEO Introduction */}
        <section className="mb-14">
          <p className="text-lg md:text-xl text-foreground-700 font-light leading-[1.8]">
            Если вы когда-либо задумывались о покупке качественной и недорогой одежды, обуви или аксессуаров напрямую из Китая, но вас пугали сложные интерфейсы агентских приложений, непонятные комиссии и языковой барьер — <strong className="font-semibold text-foreground-950">YOYBUY Spreadsheet</strong> создан именно для вас. Это инновационная система шоппинга, которая заменяет громоздкие приложения простой и интуитивно понятной электронной таблицей. В этом полном руководстве мы подробно разберём, что такое YOYBUY Spreadsheet, как он работает, чем отличается от <Link to="/" className="text-accent-700 underline hover:text-accent-800 transition-colors">Kakobuy и Superbuy</Link> и почему тысячи пользователей из России уже выбрали этот способ покупать товары из Китая.
          </p>
        </section>

        {/* Что такое YOYBUY Spreadsheet */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-[32px] font-heading font-bold text-foreground-950 mb-6">
            Что такое YOYBUY Spreadsheet
          </h2>
          <p className="text-base md:text-lg text-foreground-700 font-light leading-[1.8] mb-4">
            YOYBUY Spreadsheet — это уникальная система заказа товаров из Китая, основанная на использовании Google-таблиц (spreadsheet). В отличие от традиционных shopping-агентов, таких как Superbuy spreadsheet или Kakobuy spreadsheet, которые предлагают собственные веб-интерфейсы и мобильные приложения, YOYBUY Spreadsheet работает по принципу «одна таблица — все заказы».
          </p>
          <p className="text-base md:text-lg text-foreground-700 font-light leading-[1.8] mb-4">
            Идея проста и гениальна: вы получаете доступ к общей таблице, куда вносите ссылки на товары с любых китайских маркетплейсов — Taobao, Weidian, 1688, Pinduoduo и других. Команда YOYBUY обрабатывает каждый добавленный вами товар: проверяет наличие у продавца, выкупает, проверяет качество на складе, упаковывает и отправляет посылку по указанному вами адресу. Вам не нужно разбираться в сложных интерфейсах, скачивать приложения или проходить многоэтапную регистрацию.
          </p>
          <p className="text-base md:text-lg text-foreground-700 font-light leading-[1.8]">
            YOYBUY Spreadsheet стал особенно популярным среди пользователей из России и стран СНГ благодаря полной поддержке русского языка, низким комиссиям (ниже, чем у большинства аналогов) и максимально прозрачной системе ценообразования, где все расходы видны в одной таблице.
          </p>
        </section>

        {/* Как работает */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-[32px] font-heading font-bold text-foreground-950 mb-6">
            Как работает YOYBUY Spreadsheet
          </h2>
          <p className="text-base md:text-lg text-foreground-700 font-light leading-[1.8] mb-8">
            Процесс заказа через YOYBUY Spreadsheet состоит из четырёх простых шагов. Давайте рассмотрим каждый из них подробно.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
            {[
              { num: 1, title: 'Выбор товара', desc: 'Найдите понравившийся товар на Taobao, Weidian, 1688 или любом другом китайском маркетплейсе. Скопируйте ссылку на страницу товара, укажите нужный размер, цвет и количество.' },
              { num: 2, title: 'Добавление в spreadsheet', desc: 'Вставьте ссылку в вашу таблицу YOYBUY Spreadsheet. Укажите все детали заказа: размер, цвет, количество и особые пожелания. Таблица автоматически рассчитывает предварительную стоимость.' },
              { num: 3, title: 'Оплата', desc: 'После добавления товаров в таблицу вы производите оплату удобным для вас способом. YOYBUY поддерживает банковские карты, электронные кошельки и криптовалюту для максимального удобства.' },
              { num: 4, title: 'Доставка', desc: 'Когда все товары поступают на склад YOYBUY, команда проверяет качество, упаковывает посылку и отправляет её выбранным вами способом доставки прямо к вашей двери.' },
            ].map((step) => (
              <div key={step.num} className="flex flex-col gap-3 p-5 md:p-6 rounded-2xl bg-white border border-foreground-100/60">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 text-lg font-bold font-heading">
                  {step.num}
                </span>
                <h3 className="text-lg font-heading font-semibold text-foreground-900">{step.title}</h3>
                <p className="text-sm text-foreground-500 font-light leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Сравнение платформ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-[32px] font-heading font-bold text-foreground-950 mb-6">
            Сравнение платформ: YOYBUY vs Kakobuy vs Superbuy
          </h2>
          <p className="text-base md:text-lg text-foreground-700 font-light leading-[1.8] mb-6">
            На рынке shopping-агентов Китая существует три основных игрока, предлагающих spreadsheet-сервис. Давайте сравним их по ключевым параметрам, чтобы вы могли сделать осознанный выбор.
          </p>

          <div className="overflow-x-auto rounded-2xl border border-foreground-100/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-accent-100/60">
                  <th className="text-left p-4 font-heading font-semibold text-foreground-900">Платформа</th>
                  <th className="text-left p-4 font-heading font-semibold text-foreground-900">Удобство</th>
                  <th className="text-left p-4 font-heading font-semibold text-foreground-900">Цена</th>
                  <th className="text-left p-4 font-heading font-semibold text-foreground-900">Скорость</th>
                  <th className="text-left p-4 font-heading font-semibold text-foreground-900">Надёжность</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-foreground-100/60 bg-accent-50/30">
                  <td className="p-4 font-semibold text-foreground-950">YOYBUY</td>
                  <td className="p-4 text-accent-700 font-medium">⭐⭐⭐⭐⭐</td>
                  <td className="p-4 text-accent-700 font-medium">Низкая</td>
                  <td className="p-4 text-accent-700 font-medium">Быстрая</td>
                  <td className="p-4 text-accent-700 font-medium">Высокая</td>
                </tr>
                <tr className="border-t border-foreground-100/60">
                  <td className="p-4 font-semibold text-foreground-950">Kakobuy</td>
                  <td className="p-4 text-foreground-600">⭐⭐⭐</td>
                  <td className="p-4 text-foreground-600">Средняя</td>
                  <td className="p-4 text-foreground-600">Средняя</td>
                  <td className="p-4 text-foreground-600">Средняя</td>
                </tr>
                <tr className="border-t border-foreground-100/60">
                  <td className="p-4 font-semibold text-foreground-950">Superbuy</td>
                  <td className="p-4 text-foreground-600">⭐⭐⭐⭐</td>
                  <td className="p-4 text-foreground-600">Средняя</td>
                  <td className="p-4 text-foreground-600">Быстрая</td>
                  <td className="p-4 text-foreground-600">Высокая</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-foreground-400 font-light">
            * Данные основаны на отзывах пользователей и актуальных тарифах на июль 2026 года. Рекомендуем{' '}
            <Link to="/" className="text-accent-700 underline hover:text-accent-800 transition-colors cursor-pointer">проверить актуальные условия на главной странице</Link>.
          </p>
        </section>

        {/* Преимущества */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-[32px] font-heading font-bold text-foreground-950 mb-6">
            Преимущества YOYBUY Spreadsheet
          </h2>
          <div className="space-y-6">
            <div className="flex gap-4 p-5 rounded-2xl bg-white border border-foreground-100/60">
              <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-50">
                <i className="ri-check-line text-accent-600 text-xl"></i>
              </span>
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">Простота использования</h3>
                <p className="text-sm text-foreground-500 font-light leading-relaxed">Никаких сложных приложений — только привычная Google-таблица. Добавляйте товары, отслеживайте статусы и контролируйте бюджет в одном месте. Идеально подходит как для новичков, так и для опытных пользователей spreadsheet-шоппинга.</p>
              </div>
            </div>
            <div className="flex gap-4 p-5 rounded-2xl bg-white border border-foreground-100/60">
              <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-secondary-50">
                <i className="ri-check-line text-secondary-600 text-xl"></i>
              </span>
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">Полная поддержка на русском языке</h3>
                <p className="text-sm text-foreground-500 font-light leading-relaxed">В отличие от многих аналогов, YOYBUY Spreadsheet предлагает полную русскоязычную поддержку. Все инструкции, общение с менеджерами и интерфейс на русском языке — никакого языкового барьера.</p>
              </div>
            </div>
            <div className="flex gap-4 p-5 rounded-2xl bg-white border border-foreground-100/60">
              <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-50">
                <i className="ri-check-line text-primary-600 text-xl"></i>
              </span>
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">Быстрая обработка заказов</h3>
                <p className="text-sm text-foreground-500 font-light leading-relaxed">Команда YOYBUY обрабатывает заказы в течение нескольких часов после добавления в таблицу. Это значительно быстрее, чем у конкурентов, где обработка может занимать до нескольких дней.</p>
              </div>
            </div>
            <div className="flex gap-4 p-5 rounded-2xl bg-white border border-foreground-100/60">
              <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-50">
                <i className="ri-check-line text-accent-600 text-xl"></i>
              </span>
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-1">Низкие комиссии</h3>
                <p className="text-sm text-foreground-500 font-light leading-relaxed">YOYBUY Spreadsheet предлагает одни из самых низких комиссий на рынке spreadsheet-агентов. Все расходы прозрачны и отображаются в вашей таблице — никаких скрытых платежей или неожиданных доплат.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-[32px] font-heading font-bold text-foreground-950 mb-8">
            Часто задаваемые вопросы (FAQ)
          </h2>

          <div className="space-y-5">
            {[
              {
                q: 'Что такое spreadsheet-агент и как он работает?',
                a: 'Spreadsheet-агент — это сервис, который использует Google-таблицу как основной интерфейс для заказа товаров из Китая. Вы просто вносите ссылки на товары в таблицу, а агент (в данном случае YOYBUY) берёт на себя весь процесс: от выкупа у продавца до международной доставки. Это удобнее традиционных приложений, потому что все ваши заказы и статусы находятся в одном месте, доступном с любого устройства. YOYBUY Spreadsheet — один из лучших представителей этого формата на рынке.',
              },
              {
                q: 'Чем YOYBUY Spreadsheet отличается от Kakobuy и Superbuy?',
                a: 'Главные отличия YOYBUY Spreadsheet от Kakobuy и Superbuy: более низкие комиссии (в среднем на 15-20% ниже), полная поддержка русского языка, максимально простая система с использованием Google-таблицы вместо сложного веб-интерфейса и значительно более быстрая обработка заказов. Кроме того, YOYBUY Spreadsheet предлагает более прозрачную систему ценообразования, где все расходы отображаются непосредственно в таблице.',
              },
              {
                q: 'Можно ли доверять YOYBUY Spreadsheet свои заказы и деньги?',
                a: 'Да, YOYBUY Spreadsheet — это проверенный сервис с тысячами довольных пользователей. Команда YOYBUY работает на рынке несколько лет, имеет прозрачную систему обработки заказов и положительные отзывы от пользователей из России и стран СНГ. Вы всегда можете отслеживать статус каждого заказа в вашей таблице, что делает процесс максимально прозрачным. Рекомендуем также ознакомиться с отзывами на главной странице.',
              },
              {
                q: 'С каких маркетплейсов можно заказывать через YOYBUY Spreadsheet?',
                a: 'YOYBUY Spreadsheet поддерживает заказы со всех основных китайских маркетплейсов: Taobao, Weidian, 1688, Pinduoduo, JD.com и многих других. Вы также можете заказывать товары с небольших нишевых площадок — если у товара есть прямая ссылка, команда YOYBUY сможет его выкупить и доставить.',
              },
              {
                q: 'Сколько стоит доставка в Россию через YOYBUY Spreadsheet?',
                a: 'Стоимость доставки зависит от веса посылки, выбранного способа доставки (авиа, море, экспресс) и конечного пункта назначения в России. YOYBUY Spreadsheet предлагает несколько вариантов доставки с разными сроками и стоимостью, чтобы каждый пользователь мог выбрать оптимальный для себя вариант. Все расчёты по доставке отображаются в вашей таблице. В среднем доставка небольшой посылки (до 2 кг) авиапочтой обходится в 800-1500 рублей.',
              },
              {
                q: 'Как начать использовать YOYBUY Spreadsheet прямо сейчас?',
                a: 'Начать очень просто! Перейдите на главную страницу YOYBUY Spreadsheet по ссылке ниже, зарегистрируйтесь и получите доступ к вашей персональной таблице. После этого вы можете сразу начинать добавлять товары — первые заказы обрабатываются особенно быстро. Команда поддержки всегда готова помочь, если у вас возникнут вопросы на любом этапе.',
              },
            ].map((faq, idx) => (
              <details key={idx} className="group rounded-2xl bg-white border border-foreground-100/60 overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <span className="text-base font-heading font-semibold text-foreground-900 pr-4">
                    {faq.q}
                  </span>
                  <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-50 group-open:bg-accent-100 transition-colors">
                    <i className="ri-add-line text-accent-600 group-open:hidden"></i>
                    <i className="ri-subtract-line text-accent-600 hidden group-open:block"></i>
                  </span>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 font-light leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Внутренние ссылки */}
        <nav className="mb-8 p-6 rounded-2xl bg-background-100 border border-foreground-100/60">
          <h3 className="text-sm font-label font-semibold uppercase tracking-wider text-foreground-500 mb-3">
            Полезные ссылки
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/" className="text-sm text-accent-700 underline hover:text-accent-800 transition-colors cursor-pointer">
              ← Вернуться на главную страницу YOYBUY Beauty
            </Link>
            <span className="text-foreground-300">·</span>
            <Link to="/" className="text-sm text-accent-700 underline hover:text-accent-800 transition-colors cursor-pointer">
              Сравнение Kakobuy и Superbuy
            </Link>
            <span className="text-foreground-300">·</span>
            <Link to="/" className="text-sm text-accent-700 underline hover:text-accent-800 transition-colors cursor-pointer">
              Категории товаров из Китая
            </Link>
          </div>
        </nav>
      </div>
    </article>
  );
}

function CTABlock() {
  return (
    <section className="relative w-full min-h-[420px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20fashion%20display%20with%20beauty%20products%20and%20accessories%20on%20soft%20cream%20background%2C%20pastel%20color%20scheme%2C%20gentle%20natural%20lighting%2C%20premium%20brand%20lifestyle%20photography%2C%20clean%20and%20airy%20aesthetic%2C%20scandinavian%20minimalism%2C%20soft%20shadows%20and%20warm%20tones&width=1600&height=600&seq=article-cta-yoybuy&orientation=landscape"
          alt="Начните использовать YOYBUY Spreadsheet"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/35 via-white/15 to-white/30"></div>
      </div>

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 md:px-10 py-20 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
        <div className="flex flex-col gap-6 max-w-[480px]">
          <h2 className="text-4xl md:text-[60px] font-heading font-bold text-foreground-950 lowercase leading-none">
            готовы начать?
          </h2>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="group inline-flex items-center gap-3 h-[56px] px-8 rounded-full bg-white border border-foreground-200/60 text-foreground-800 text-base font-medium hover:bg-accent-50 hover:border-accent-300 transition-all duration-300 cursor-pointer whitespace-nowrap self-start"
          >
            Buy It Now
            <span className="w-5 h-5 rounded-full bg-accent-500 flex items-center justify-center group-hover:bg-accent-600 transition-colors">
              <i className="ri-arrow-right-line text-white text-xs"></i>
            </span>
          </a>
        </div>

        <div className="max-w-[360px]">
          <p className="text-lg md:text-xl text-foreground-800 font-light leading-relaxed">
            Быстрая доставка в Россию и страны СНГ. Поддержка 24/7. Начните шоппинг с YOYBUY Spreadsheet уже сегодня и откройте для себя мир качественных товаров из Китая.
          </p>
        </div>
      </div>
    </section>
  );
}